// Форматирование и подписи на русском.
import type {
  AgentKind,
  AgentStatus,
  ChangeEvent,
  EscalationType,
  IntentKind,
  JobStatus,
  JobTrigger,
  KnowledgeKind,
  MessageKind,
  Priority,
  SafetyMode,
  TaskKind,
  TaskStatus,
  Tone,
} from '../types.ts'

export function plural(n: number, forms: [string, string, string]): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return forms[2]
  if (b > 1 && b < 5) return forms[1]
  if (b === 1) return forms[0]
  return forms[2]
}

function parse(iso: string | null | undefined): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  return Number.isNaN(t) ? null : t
}

const dateFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' })
const dateYearFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
const dateTimeFmt = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})
const timeFmt = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })

export function formatDate(iso: string | null | undefined): string {
  const t = parse(iso)
  if (t === null) return '—'
  const d = new Date(t)
  return (d.getFullYear() === new Date().getFullYear() ? dateFmt : dateYearFmt).format(d).replace('.', '')
}

export function formatDateTime(iso: string | null | undefined): string {
  const t = parse(iso)
  if (t === null) return '—'
  return dateTimeFmt.format(new Date(t)).replace('.', '')
}

export function formatTime(iso: string | null | undefined): string {
  const t = parse(iso)
  return t === null ? '—' : timeFmt.format(new Date(t))
}

/** «только что», «5 мин назад», «3 ч назад», «вчера», «4 дня назад», «12 окт». Будущее — «через 5 мин». */
export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  const t = parse(iso)
  if (t === null) return '—'
  const sec = Math.round((now - t) / 1000)
  if (sec < -30) return timeUntil(iso, now)
  if (sec < 45) return 'только что'
  const min = Math.round(sec / 60)
  if (min < 60) return `${Math.max(1, min)} мин назад`
  const h = Math.round(min / 60)
  if (h < 24) return `${h} ч назад`
  const d = Math.round(h / 24)
  if (d === 1) return 'вчера'
  if (d < 7) return `${d} ${plural(d, ['день', 'дня', 'дней'])} назад`
  return formatDate(iso)
}

export function timeUntil(iso: string | null | undefined, now: number = Date.now()): string {
  const t = parse(iso)
  if (t === null) return '—'
  const sec = Math.round((t - now) / 1000)
  if (sec <= 0) return 'истекло'
  if (sec < 60) return 'меньше минуты'
  const min = Math.round(sec / 60)
  if (min < 60) return `через ${min} мин`
  const h = Math.round(min / 60)
  if (h < 24) return `через ${h} ч`
  const d = Math.round(h / 24)
  return `через ${d} ${plural(d, ['день', 'дня', 'дней'])}`
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—'
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s} с`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} мин ${String(s % 60).padStart(2, '0')} с`
  const h = Math.floor(m / 60)
  return `${h} ч ${String(m % 60).padStart(2, '0')} мин`
}

export function jobDurationMs(
  job: { started_at: string | null; finished_at: string | null },
  now: number = Date.now(),
): number | null {
  const s = parse(job.started_at)
  if (s === null) return null
  const f = parse(job.finished_at)
  return (f ?? now) - s
}

export function formatUsd(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  if (n === 0) return '$0'
  if (n > 0 && n < 0.01) return '<$0.01'
  if (n < 1000) return `$${n.toFixed(2)}`
  return `$${Math.round(n).toLocaleString('ru-RU')}`
}

export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  if (n < 1000) return String(n)
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0).replace('.', ',')} тыс`
  return `${(n / 1_000_000).toFixed(1).replace('.', ',')} млн`
}

export function truncate(s: string, n: number): string {
  if (s.length <= n) return s
  return `${s.slice(0, n - 1).trimEnd()}…`
}

// ───────── подписи ─────────

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
  urgent: 'Срочный',
}
export const PRIORITY_ORDER: Record<Priority, number> = { urgent: 0, high: 1, normal: 2, low: 3 }

export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  task: 'задача',
  cross: 'сквозная',
  question: 'вопрос',
  incident: 'инцидент',
  review: 'проверка',
  docs: 'справочник',
  routine: 'регулярная',
}

export const STATUS_TONES: Record<TaskStatus, Tone> = {
  inbox: 'neutral',
  triage: 'neutral',
  in_progress: 'neutral',
  waiting_agents: 'neutral',
  waiting_human: 'warn',
  review: 'neutral',
  done: 'ok',
  cancelled: 'neutral',
}

export const MESSAGE_KIND_LABELS: Record<MessageKind, string> = {
  note: 'заметка',
  question: 'вопрос',
  answer: 'ответ',
  impact: 'влияние',
  handoff: 'передача',
  status: 'статус',
  escalation: 'эскалация',
  decision: 'решение',
  change: 'изменение',
  review: 'ревью',
  comment: 'комментарий',
  result: 'результат',
  supervise: 'разбор руководителем',
}
export const MESSAGE_KIND_TONES: Record<MessageKind, Tone> = {
  note: 'neutral',
  question: 'neutral',
  answer: 'neutral',
  impact: 'warn',
  handoff: 'neutral',
  status: 'neutral',
  escalation: 'warn',
  decision: 'neutral',
  change: 'neutral',
  review: 'neutral',
  comment: 'neutral',
  result: 'ok',
  supervise: 'warn',
}

export const AGENT_KIND_LABELS: Record<AgentKind, string> = {
  orchestrator: 'Штаб',
  lead: 'Лид направления',
  service: 'Сервисный',
  reviewer: 'Проверяющий',
  librarian: 'Библиотекарь',
  worker: 'Исполнитель',
  external: 'Внешний',
}
export const AGENT_KIND_TONES: Record<AgentKind, Tone> = {
  orchestrator: 'neutral',
  lead: 'neutral',
  service: 'neutral',
  reviewer: 'neutral',
  librarian: 'neutral',
  worker: 'neutral',
  external: 'neutral',
}

export const AGENT_STATUS_LABELS: Record<AgentStatus, string> = {
  idle: 'свободен',
  busy: 'работает',
  paused: 'на паузе',
}

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  queued: 'в очереди',
  running: 'работает',
  waiting: 'ждёт',
  done: 'готово',
  failed: 'ошибка',
  cancelled: 'отменён',
}
export const JOB_STATUS_TONES: Record<JobStatus, Tone> = {
  queued: 'neutral',
  running: 'ok',
  waiting: 'warn',
  done: 'neutral',
  failed: 'danger',
  cancelled: 'neutral',
}

export const JOB_TRIGGER_LABELS: Record<JobTrigger, string> = {
  task: 'задача',
  message: 'сообщение',
  answer: 'ответ человека',
  review: 'проверка',
  wake: 'пробуждение',
  nudge: 'напоминание',
}

export const ESCALATION_TONES: Record<EscalationType, Tone> = {
  business_decision: 'neutral',
  clarification: 'neutral',
  approval: 'warn',
  blocker: 'danger',
  message: 'warn',
  action: 'warn',
}

export const KNOWLEDGE_KIND_LABELS: Record<KnowledgeKind, string> = {
  note: 'заметка',
  rule: 'правило',
  fact: 'факт',
  howto: 'инструкция',
  incident: 'инцидент',
  adr: 'ADR',
  process: 'процесс',
}

export const INTENT_KIND_LABELS: Record<IntentKind, string> = {
  code: 'код',
  config: 'конфиг',
  migration: 'миграция',
  'prod-op': 'операция на проде',
  deploy: 'деплой',
  data: 'данные',
  contract: 'контракт',
}
export const INTENT_KIND_TONES: Record<IntentKind, Tone> = {
  code: 'neutral',
  config: 'neutral',
  migration: 'warn',
  'prod-op': 'warn',
  deploy: 'warn',
  data: 'warn',
  contract: 'neutral',
}

export const CHANGE_SOURCE_LABELS: Record<ChangeEvent['source'], string> = {
  'github-commit': 'коммит',
  'github-pr': 'PR',
  deploy: 'деплой',
  agent: 'агент',
  human: 'человек',
}

export const SCOPE_LABELS: Record<'global' | 'direction' | 'service' | 'agent', string> = {
  global: 'Вся компания',
  direction: 'Направление',
  service: 'Сервис',
  agent: 'Агент',
}

export const SAFETY_TONES: Record<SafetyMode, Tone> = {
  read_only: 'neutral',
  pr_only: 'warn',
  full: 'warn',
}

export const SAFETY_EXPLANATIONS: Record<SafetyMode, string> = {
  read_only: 'Агенты только читают код, базы и логи и предлагают изменения. Ничего не пишут и не выкатывают.',
  pr_only: 'Агенты работают в ветках и открывают PR, но не мержат и не трогают прод. Выкатка — за человеком.',
  full: 'Агенты могут мержить и действовать на проде, но каждое действие на проде проходит через твоё одобрение.',
}

export const RISK_LABELS: Record<'low' | 'medium' | 'high', string> = {
  low: 'низкий риск',
  medium: 'средний риск',
  high: 'высокий риск',
}

/** «230 КБ», «4.2 МБ» — размер файла. */
export function formatSize(n: number): string {
  if (n < 1024) return `${n} Б`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} КБ`
  return `${(n / 1024 / 1024).toFixed(1)} МБ`
}
